import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormikContext } from 'formik';
import GenericTabs from 'components/navigation/Tabs/Tabs';
import { FormSectionSimple } from 'components/layout/Section/FormSectionSimple';
import { InputFactory } from 'factories/InputFactory';
import { DocumentVerificationStatusEnum, FileTypeEnum } from 'api/actor';
import { ContextEnum } from 'api/files';
import { NsKey } from 'enums/NsKey';
import { useOnChange } from 'hooks/useOnChange';
import { selectIsCniVerified } from './borrowerInsuredWithDocuments.selectors';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { FileText, ClipboardList, User } from 'lucide-react';
import { DocumentsFormFieldNames } from 'features/new-actor/enums/documentsFormFieldNames';
import { PersonalInformationFieldNames } from 'features/new-actor/enums/personalInformationFieldNames';
import { AddressFormFieldNames } from 'shared/enums/addressFormFieldNames';
import { NaturalPersonFormFieldNames } from 'features/new-actor/enums/naturalPersonFormFieldNames';
import {
  RequiredTabsConfig,
  TabFieldsConfig,
  useTabErrors
} from 'components/navigation/Tabs/useTabErrors';
import { FormikCheckbox } from 'components/forms/formik/FormikCheckbox';
import { BorrowerInsuredFormFieldNames } from './borrowerInsuredForm';
import {
  BROKER_CONFIRMATION_FIELD,
  BorrowerInsuredWithDocumentsFormValues
} from './borrowerInsuredWithDocuments.config';
import { borrowerNaturalInputsConfig } from './natural/borrowerNatural.inputs';
import { BorrowerSurveyLayout } from './shared/BorrowerSurveyLayout';
import { naturalPersonWithDocumentsInputsConfig } from 'features/new-actor/form/inputs-config/naturalPersonWithDocumentsInputsConfig';

interface BorrowerInsuredWithDocumentsLayoutProps {
  personId: string;
  onIdentityStatusChanged?: () => void;
  requiresBrokerConfirmation?: boolean;
}

const TAB_FIELDS_CONFIG: TabFieldsConfig<BorrowerInsuredWithDocumentsFormValues> =
  {
    1: [
      PersonalInformationFieldNames.Civility,
      PersonalInformationFieldNames.LastName,
      PersonalInformationFieldNames.FirstName,
      PersonalInformationFieldNames.BirthName,
      PersonalInformationFieldNames.BirthCountry,
      PersonalInformationFieldNames.Nationality,
      PersonalInformationFieldNames.BirthDate,
      PersonalInformationFieldNames.Email,
      PersonalInformationFieldNames.Phone,
      PersonalInformationFieldNames.TaxResidenceCountryId,
      PersonalInformationFieldNames.IsPPE,
      PersonalInformationFieldNames.IsFPE,
      AddressFormFieldNames.Address,
      AddressFormFieldNames.AdditionalAddress,
      AddressFormFieldNames.PostalCode,
      AddressFormFieldNames.City,
      AddressFormFieldNames.Country,
      BorrowerInsuredFormFieldNames.Job,
      BorrowerInsuredFormFieldNames.ProspectType
    ],
    2: [BorrowerInsuredFormFieldNames.SurveyForm],
    3: [DocumentsFormFieldNames.Documents]
  };

const REQUIRED_TABS_CONFIG: RequiredTabsConfig = {
  2: true,
  3: true
};

export const BorrowerInsuredWithDocumentsLayout: React.FC<
  BorrowerInsuredWithDocumentsLayoutProps
> = ({ personId, onIdentityStatusChanged, requiresBrokerConfirmation = false }) => {
  const [activeTab, setActiveTab] = useState<number | string>(1);
  const { t } = useDynamicTranslation([
    NsKey.Borrower,
    NsKey.ActorForm,
    NsKey.Common
  ]);
  const { errors, setFieldTouched, values } = useFormikContext<BorrowerInsuredWithDocumentsFormValues>();

  useOnChange(selectIsCniVerified(values), onIdentityStatusChanged);

  const { getTabNotification, touchFieldsWithErrors, touchFieldsWithErrorsInTab } = useTabErrors(
    errors,
    TAB_FIELDS_CONFIG,
    REQUIRED_TABS_CONFIG,
    setFieldTouched
  );

  // Marquer les champs avec erreurs comme touched au montage et quand les erreurs changent
  useEffect(() => {
    touchFieldsWithErrors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  const handleTabChange = useCallback((tabValue: number | string) => {
    setActiveTab(tabValue);
    touchFieldsWithErrorsInTab(tabValue);
  }, [touchFieldsWithErrorsInTab]);

  // Disabled fields for edit mode
  const naturalPersonDisabledFields = useMemo(
    () => [
      PersonalInformationFieldNames.LastName,
      PersonalInformationFieldNames.FirstName,
      PersonalInformationFieldNames.Civility
    ],
    []
  );

  // Documents configuration
  const documentsToProvide = useMemo(() => {
    const baseDocuments: Array<{
      name: string;
      fileType: FileTypeEnum;
      fieldName: string;
      context: ContextEnum;
      entityId: string;
    }> = [
      {
        name: 'DOCUMENT_TYPE.ID_CARD',
        fileType: FileTypeEnum.PieceIdentite,
        fieldName: `${DocumentsFormFieldNames.Documents}.${DocumentsFormFieldNames.Cni}`,
        context: ContextEnum.Person,
        entityId: personId
      }
    ];

    return [
      {
        id: 'identity',
        label: 'DOCUMENT_SECTION.DOCUMENTS_TO_PROVIDE',
        documents: baseDocuments
      }
    ];
  }, [personId]);

  // Inputs config with documents
  const documentsInputsConfig = useMemo(
    () => ({
      ...naturalPersonWithDocumentsInputsConfig,
      [DocumentsFormFieldNames.Documents]: {
        ...naturalPersonWithDocumentsInputsConfig.documents,
        sections: documentsToProvide
      }
    }),
    [documentsToProvide]
  );

  const renderPersonalField = useCallback(
    (fieldName: any) => {
      return (
        <InputFactory
          config={borrowerNaturalInputsConfig}
          fieldName={fieldName}
          disabledFields={naturalPersonDisabledFields}
        />
      );
    },
    [naturalPersonDisabledFields]
  );

  const renderDocumentField = useCallback(
    (fieldName: string) => (
      <InputFactory config={documentsInputsConfig} fieldName={fieldName} />
    ),
    [documentsInputsConfig]
  );

  return (
    <div className="flex flex-col">
      <GenericTabs defaultTab={activeTab} onChange={handleTabChange}>
      <GenericTabs.Item
        label={t('COMMON.LABELS.INFORMATIONS')}
        value={1}
        icon={<User className="w-4 h-4" />}
        notification={getTabNotification(1)}
      />
      <GenericTabs.Item
        label={t('COMMON.LABELS.SITUATION')}
        value={2}
        icon={<ClipboardList className="w-4 h-4" />}
        notification={getTabNotification(2)}
      />
      <GenericTabs.Item
        label={t('COMMON.LABELS.DOCUMENTS')}
        value={3}
        icon={<FileText className="w-4 h-4" />}
        notification={getTabNotification(3)}
      />

      {/* Tab 1: Personal Information */}
      <GenericTabs.Content value={1}>
        <div className="font-lato flex flex-col gap-4">
          <FormSectionSimple title={t('ACTOR_FORM.LABELS.CONTACT_SECTION')}>
            <>
              <div className="w-full col-span-6 flex gap-4">
                <div className="w-32">
                  {renderPersonalField(PersonalInformationFieldNames.Civility)}
                </div>
                <div className="flex-grow">
                  {renderPersonalField(PersonalInformationFieldNames.LastName)}
                </div>
              </div>
              <div className="col-span-3">
                {renderPersonalField(PersonalInformationFieldNames.FirstName)}
              </div>
              <div className="col-span-3">
                {renderPersonalField(PersonalInformationFieldNames.BirthName)}
              </div>
              <div className="col-span-3">
                {renderPersonalField(PersonalInformationFieldNames.BirthCountry)}
              </div>
              <div className="col-span-3">
                {renderPersonalField(PersonalInformationFieldNames.Nationality)}
              </div>
              <div className="col-span-3">
                {renderPersonalField(PersonalInformationFieldNames.BirthDate)}
              </div>
              <div className="col-span-3">
                {renderPersonalField(PersonalInformationFieldNames.Email)}
              </div>
              <div className="col-span-3">
                {renderPersonalField(PersonalInformationFieldNames.Phone)}
              </div>
              <div className="col-span-3">
                {renderPersonalField(
                  NaturalPersonFormFieldNames.TaxResidenceCountryId
                )}
              </div>
              <div className="col-span-3">
                {renderPersonalField(BorrowerInsuredFormFieldNames.Job)}
              </div>
              <div className="col-span-3">
                {renderPersonalField(BorrowerInsuredFormFieldNames.ProspectType)}
              </div>
              <div className="col-span-3">
                {renderPersonalField(PersonalInformationFieldNames.IsPPE)}
              </div>
              <div className="col-span-3">
                {renderPersonalField(BorrowerInsuredFormFieldNames.IsFPE)}
              </div>
            </>
          </FormSectionSimple>
          <FormSectionSimple title={t('ACTOR_FORM.LABELS.COORDINATE_SECTION')}>
            <>
              <div className="col-span-3">
                {renderPersonalField(AddressFormFieldNames.Address)}
              </div>
              <div className="col-span-3">
                {renderPersonalField(AddressFormFieldNames.AdditionalAddress)}
              </div>
              <div className="col-span-3">
                {renderPersonalField(AddressFormFieldNames.PostalCode)}
              </div>
              <div className="col-span-3">
                {renderPersonalField(AddressFormFieldNames.City)}
              </div>
              <div className="col-span-3">
                {renderPersonalField(AddressFormFieldNames.Country)}
              </div>
            </>
          </FormSectionSimple>
        </div>
      </GenericTabs.Content>

      {/* Tab 2: Situation/Survey */}
      <GenericTabs.Content value={2}>
        <BorrowerSurveyLayout />
      </GenericTabs.Content>

      {/* Tab 3: Documents */}
      <GenericTabs.Content value={3}>
        <div className="p-4">
          {renderDocumentField(DocumentsFormFieldNames.Documents)}
        </div>
      </GenericTabs.Content>
    </GenericTabs>
      {requiresBrokerConfirmation && (
        <div className="sticky bottom-0 left-0 right-0 -mx-4 px-4 py-3 bg-white/95 backdrop-blur border-t border-primary/20 shadow-[0_-4px_12px_rgba(0,0,0,0.04)] z-10">
          <FormikCheckbox
            name={BROKER_CONFIRMATION_FIELD}
            label="Je confirme avoir vérifié et validé avec le client l'ensemble des informations saisies dans ce formulaire, et notamment avoir posé la question de savoir s'il est une personne politiquement exposée."
            required
          />
        </div>
      )}
    </div>
  );
};
